import { Chip, Stack, Typography, useTheme } from '@mui/material'
import React, { FC, ReactElement, useState } from 'react'
import { RightSlider } from '../../../../../components/sliders'
import { Table } from '../../../../../components/table'
import { IPagingResponse } from '../../../../../types/Requests'
import { GrantOutcomeDropdown } from './GrantOutcome'
import { LikeDislikeWidget } from './LikeDislikeWidget'
import { ProjectGrant } from './ProjectGrant'
import { TablePaging } from './TablePaging'
import { TrackPipelineStages } from './TrackPipelineStages'
import { BuildingIcon, GrowthChart } from '../../../../../components/icons'
import { AgencyTag } from './AgencyTag'
import { useDispatch } from 'react-redux'
import { AppDispatch } from '../../../../../store'
import { GrantMatchActions } from '../../../../../store/grantMatcher'

interface GrantsTableProps {
  grants: any[]
  currentTab: string
  projectId: string
  tableHeader: string[]
  fetchGrants: () => void
  paging?: IPagingResponse
  emptyComponent?: ReactElement
}

export const GrantsTable: FC<GrantsTableProps> = ({
                                                    grants,
                                                    paging,
                                                    projectId,
                                                    fetchGrants,
                                                    tableHeader,
                                                    currentTab,
                                                    emptyComponent,
                                                  }) => {
  const theme = useTheme()

  const dispatch = useDispatch<AppDispatch>()

  const [selectedGrant, setSelectedGrant] = useState('')
  const [showGrantDetail, setShowGrantDetail] = useState(false)

  const headerCells = tableHeader.map((header, index) => {
    return {
      content: (
        <Typography key={index} color="secondary" fontWeight={500} fontSize="0.9rem">
          {header}
        </Typography>
      ),
    }
  })

  const handleLikeDislikeAction = async (refId: string, bodyPayload: any) => {
    const likedAction = await dispatch(
      GrantMatchActions.updateMatchedTechGrantAsync({
        params: { techId: projectId, refId },
        body: bodyPayload,
      }),
    )

    if (GrantMatchActions.updateMatchedTechGrantAsync.fulfilled.match(likedAction)) {
      await fetchGrants()
    }
  }

  const handleProgressStatusUpdate = async (refId: string, stage: string) => {
    await dispatch(
      GrantMatchActions.updateMatchedTechGrantAsync({
        params: { techId: projectId, refId },
        body: { progressStatus: stage }
      }),
    )

    await fetchGrants()
  }

  const rows: any[] =
    grants &&
    grants.map((rowCell: any) => {
      const cells = [
        {
          content: (
            <Typography
              color="secondary"
              fontSize="0.875rem"
              fontWeight={theme.typography.fontWeightMedium}
            >
              {rowCell.title || rowCell.topicTitle}
            </Typography>
          ),
          cellProps: {
            sx: { maxWidth: 300 },
          },
        },
        {
          content: (
            <AgencyTag value={rowCell.agency} />
          ),
        },
        {
          content: (
            <Typography
              fontSize="0.875rem"
              color="secondary.light"
              fontWeight={theme.typography.fontWeightMedium}
            >
              ${rowCell.formattedAwardAmount}
            </Typography>
          ),
          cellProps: {
            sx: { display: { xs: 'none', sm: 'table-cell' } },
          },
        },
        {
          content: (
            <Chip
              label={rowCell.grantType}
              icon={rowCell.grantType === 'Small business' ? <GrowthChart /> : <BuildingIcon />}
              sx={{
                mb: 0.5,
                height: '36px',
                width: 'min-content',
                fontSize: '0.75rem',
                color: rowCell.grantType === 'Small business' ? 'success.dark' : 'info.dark',
                backgroundColor: rowCell.grantType === 'Small business' ? 'success.light' : 'info.light',
              }}
            />
          ),
          cellProps: {
            sx: { display: { xs: 'none', sm: 'table-cell' } },
          },
        },
        {
          content: (
            <Typography
              fontSize="0.875rem"
              color="secondary.light"
              fontWeight={theme.typography.fontWeightMedium}
            >
              {rowCell.closeDate}
            </Typography>
          ),
          cellProps: {
            sx: { display: { xs: 'none', sm: 'table-cell' } },
          },
        },
        (currentTab === 'Matches' || currentTab === 'Rejected') && {
          content: <LikeDislikeWidget
                      key={rowCell.refId}
                      like={rowCell.like}
                      handleLikeDislike={(body) => handleLikeDislikeAction(rowCell.refId, body)}
                    />,
          cellProps: {
            sx: { display: { xs: 'none', sm: 'table-cell' } },
          },
        },

        currentTab === 'Submitted' && {
          content: (
            <GrantOutcomeDropdown
              cb={fetchGrants}
              projectId={projectId}
              currentOutcome={rowCell.outcome}
              refId={rowCell.refId}
            />
          ),
          cellProps: {
            sx: { display: { xs: 'none', sm: 'table-cell' } },
          },
        },

        currentTab === 'Pipeline' && {
          content: (
            <TrackPipelineStages
              key={rowCell.refId}
              currentStage={rowCell.progressStatus}
              onChange={(stage) => handleProgressStatusUpdate(rowCell.refId, stage)}
            />
          ),
          cellProps: {
            sx: { display: { xs: 'none', sm: 'table-cell' } },
          },
        },
      ].filter(Boolean)

      return {
        rowProps: {
          onClick: () => {
            setSelectedGrant(rowCell.topicId)
            setShowGrantDetail(true)
          },
          sx: { '&:hover': { cursor: 'pointer', background: theme.palette.grey[100] } },
        },
        cells,
      }
    })

  return (
    <>
      <RightSlider
        open={showGrantDetail}
        setOpen={setShowGrantDetail}
        closeButtonAction={() => setShowGrantDetail(false)}
      >
        <ProjectGrant projectId={projectId} refId={selectedGrant} />
      </RightSlider>

      <Stack
        sx={{
          my: '1rem',
          borderRadius: '8px',
          background: 'white',
        }}
        justifyContent="space-between"
      >
        {!!rows.length ? (
          <>
            <Table
              containerProps={{
                sx: {
                  boxShadow: 'none',
                  background: 'transparent',
                },
              }}
              head={{
                containerProps: {
                  sx: { borderRadius: '8px', background: theme.palette.grey[200], width: '100%' },
                },
                rows: [{ cells: headerCells || [] }],
              }}
              body={{ rows }}
            />

            {paging && <TablePaging paging={paging} />}
          </>
        ) : (
          emptyComponent ?? <></>
        )}
      </Stack>
    </>
  )
}
